import _ from "lodash";
import { addOwnerToItem } from "../../utils";
import { IDateRange } from "./Components/DateRangeSelector";
import { format } from "date-fns";
import { json2csv } from "json-2-csv";

export const chartColors = [
  "#003f5c",
  "#444e86",
  "#955196",
  "#dd5182",
  "#ff6e54",
  "#ffa600",
];

export interface IAppData {
  appId: string;
  devices: string[];
}

export interface ILogTimeData {
  year: number;
  month: number;
  day: number;
  hrs: number;
  min: number;
  sec: number;
  timeString: string;
  date: Date;
}

export interface ILogItemData {
  model: string;
  id?: number;
  level: number;
}

export interface ILogRowData {
  time?: ILogTimeData;
  action?: string; // button action
  button?: string; // button name
  path?: string; // item path
  info?: string; // other information
  models?: string; // model information
  items?: string; // item information
  logItemData?: ILogItemData[];
  logItemUrl?: string;
}

export interface IFilterData {
  action: string[];
  button: string[];
  path: string[];
  info: string[];
}

export interface IAllFilterData extends IFilterData {
  models: string[];
  items: string[];
}

export const defaultFilters = {
  action: [],
  button: [],
  path: [],
  info: [],
  models: [],
  items: [],
};

export const defaultFilterLabels = {
  action: "Actions",
  button: "Button Name",
  path: "Content Path",
  pathInfo: "Content Groups",
  info: "Other Info",
};

export const checkUniqueData = (
  row: ILogRowData,
  stat: IFilterData,
  checkKey: keyof IFilterData
): string[] => {
  const rowString = row[checkKey];
  if (!rowString || _.includes(stat[checkKey], rowString))
    return stat[checkKey];
  return [...stat[checkKey], rowString];
};

export const convertTime = (stamp: string): ILogTimeData => {
  const year = _.toNumber(stamp.slice(0, 4));
  const month = _.toNumber(stamp.slice(4, 6));
  const day = _.toNumber(stamp.slice(6, 8));

  const hrs = _.toNumber(stamp.slice(8, 10));
  const min = _.toNumber(stamp.slice(10, 12));
  const sec = _.toNumber(stamp.slice(12, 14));
  const timeString = `${hrs}:${min}:${sec}`;
  const date = new Date(year, month - 1, day, hrs, min, sec);
  return {
    year,
    month,
    day,
    hrs,
    min,
    sec,
    date,
    timeString,
  };
};

export const displayTime = (time: ILogTimeData | null) => {
  if (!time) return "";
  const { day, month, year, timeString } = time;
  return `${day}-${month}-${year} ${timeString}`;
};

/**
 * Parses data from analytics path string
 * @param pathData string in this format: modelName>itemID>modelName>itemID
 * @returns [ arrayOfRawData, url ]
 */
export const getItemDataFromPath = (
  pathData: string
): [ILogItemData[] | undefined, string | undefined] => {
  // split data
  const dataArray: string[] = pathData.split(">");
  if (pathData.length < 1) return [undefined, undefined];

  /*
  // LEGACY METHOD - combine models and ids
  // This method reads model>id>model>id...
  const itemData: ILogItemData[] = dataArray.reduce(
    (arr: ILogItemData[], d: string, index) => {
      const level = Math.floor(index / 2);
      if (index % 2 === 0) return [...arr, { model: d, level }];
      const val = arr.pop();
      if (val) return [...arr, { ...val, id: _.toNumber(d) }];
      else return arr;
    },
    []
  );
  */

  // combine models and ids
  // This method reads model:id>model:id
  const itemData: ILogItemData[] = dataArray.reduce(
    (arr: ILogItemData[], d: string, index) => {
      // const level = Math.floor(index / 2);
      // if (index % 2 === 0) return [...arr, { model: d, level }];
      // const val = arr.pop();
      // if (val) return [...arr, { ...val, id: _.toNumber(d) }];
      // else return arr;
      const getModelAndID = d.split(":");
      if (getModelAndID.length === 2)
        return [
          ...arr,
          {
            model: getModelAndID[0],
            id: _.toNumber(getModelAndID[1]),
            level: index,
          },
        ];
      return arr;
    },
    []
  );

  const fromItem =
    itemData.length > 1 ? itemData[itemData.length - 2] : undefined;
  const fromParams = fromItem
    ? `?from=${fromItem.model}&id=${fromItem.id}`
    : ``;
  const thisItem = _.last(itemData);
  const itemIdSuffix = thisItem ? `/show/${thisItem.id}` : ``;
  const url = thisItem
    ? `/m/${thisItem.model}${itemIdSuffix}${fromParams}`
    : `/`;
  return [itemData, url];
};

export const getFilterData = (rows: ILogRowData[]) => {
  const emptyFilters = _.cloneDeep(defaultFilters);
  return rows.reduce((stats: IAllFilterData, row: ILogRowData) => {
    const action = checkUniqueData(row, stats, "action");
    const button = checkUniqueData(row, stats, "button");
    const path = checkUniqueData(row, stats, "path");
    const info = checkUniqueData(row, stats, "info");
    const { models, items } = checkUniqueModelsAndItems(
      row.logItemData || [],
      stats.models,
      stats.items
    );
    items.sort((a, b) => a.localeCompare(b));
    return { action, button, path, info, models, items };
  }, emptyFilters);
};

/**
 * Get unique models and items for listings
 * @param logItemData log data
 * @param models all models
 * @param items all items
 * @returns
 */
export const checkUniqueModelsAndItems = (
  logItemData: ILogItemData[],
  models: string[],
  items: string[]
) => {
  const modelUniques = logItemData.reduce((arr, item) => {
    const modelName = item.model;
    if (!_.includes(arr, modelName)) arr.push(modelName);
    return arr;
  }, models);

  const itemUniques = logItemData.reduce((arr, item) => {
    const modelAndId = `${item.model}:${item.id}`;
    if (!_.includes(arr, modelAndId)) arr.push(modelAndId);
    return arr;
  }, items);

  return { models: modelUniques, items: itemUniques };
};

/**
 *
 * @param rowData
 * @param model
 * @returns
 */
export const checkModels = (rowData: ILogRowData, model: string) => {
  return (
    rowData.logItemData?.find((logItem) => logItem.model === model) !==
    undefined
  );
};

export const checkItems = (rowData: ILogRowData, item: string) => {
  return (
    rowData.logItemData?.find((logItem: ILogItemData) => {
      const modelString = `${logItem.model}:${logItem.id}`;
      return item === modelString;
    }) !== undefined
  );
};

export const addOwnerItemToArray = async (data: any[]) => {
  const promises = data.map((item) => addOwnerToItem(item));
  try {
    const responses = await Promise.all(promises);
    return responses;
  } catch (err) {
    console.log("Error getting owners for media items");
    return data;
  }
};

interface IAnalyticsSearchParams {
  startRange: IDateRange | null;
  appId: string | null;
}

/**
 * @returns returns the analytics params on a page
 */
export const useAnalyticsParams = () => {
  const qryParams = new URLSearchParams(window.location.search);
  const startStr = qryParams.get("start_date");
  const endStr = qryParams.get("end_date");
  const startDate = startStr ? new Date(startStr) : null;
  const endDate = endStr ? new Date(endStr) : null;
  const dateRange: IDateRange | null =
    endDate && startDate
      ? {
          startDate,
          endDate,
        }
      : null;

  const app = qryParams.get("app");

  // If params are needed
  // const dateParams =
  //   startStr && endStr ? `start_date=${startStr}&end_date=${endStr}` : ``;
  // const appParams = app ? `app_id=${app}` : ``;
  // const urlParams = `?${dateParams}${dateParams ? "&" : ""}${appParams}`;

  const params: IAnalyticsSearchParams = {
    startRange: dateRange,
    appId: app,
  };

  return params;
};

/**
 * @param dateRange IDateRange
 * @param appId app ID as per the id_list in the backend
 * @returns returns a function to update the analytic params
 */
export const useUpdateAnalyticsParams = () => {
  return (dateRange: IDateRange, appId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set(
      "start_date",
      format(dateRange.startDate, "yyyy-MM-dd")
    );
    url.searchParams.set("end_date", format(dateRange.endDate, "yyyy-MM-dd"));
    url.searchParams.set("app", appId);
    window.history.replaceState(null, "", url);
  };
};

/**
 * Download a string as a CSV file
 * @param text text to be saved
 * @param filename filename output
 */
export const handleTextToDownload = (text: string, filename: string) => {
  const element = document.createElement("a");
  const file = new Blob([text], {
    type: "text/plain",
  });
  element.href = URL.createObjectURL(file);
  element.download = filename;
  document.body.appendChild(element);
  element.click();
};

/**
 * Convert object array of analytics log data to be downloaded as csv
 * @param data JSON log data
 */
export const downloadJsonAsCsv = (data: any[], filename: string) => {
  const hostSite = window.location.host;
  const updateLinkData = data.map((d) => {
    const cleansedData = _.omit(d, ["logItemUrl", "action", "info", "path"]);
    if (d.link)
      return {
        ...cleansedData,
        link: hostSite + d.link,
      };
    if (d.logItemUrl)
      return {
        ...cleansedData,
        link: hostSite + d.logItemUrl,
      };
    return cleansedData;
  });

  // generate filename
  const csvData = json2csv(updateLinkData);

  // make fake download click
  const element = document.createElement("a");
  const file = new Blob([csvData], {
    type: "text/plain",
  });
  element.href = URL.createObjectURL(file);
  element.download = filename;
  document.body.appendChild(element);
  element.click();
};

/**
 * generate csv filename from prefix, dates and appId
 * @param prefix type of chart or data
 * @param dates date range
 * @param appId app id as of id_list
 * @returns string of csv filename
 */
export const getCsvFilename = (
  dates: IDateRange,
  appId: string,
  prefix?: string
) => {
  const id = appId.replaceAll(" ", "-");
  const df = (date: Date) => format(date, "yyyy-MM-dd");
  return `${prefix ? prefix + "_" : ""}${id}_${df(dates.startDate)}-${df(
    dates.endDate
  )}.csv`;
};
